import { AuditHistory } from '@services/api/deckManagement/graphql/schema/API';
import { ReactNode } from 'react';
import { HistoryItemMetadata } from 'types/AuditHistory.types';
import { DeleteDecklineData, EnhancedDeckline } from 'types/DecklinesContext.types';
import { DeleteTOCEntryData, EnhancedTOCEntry } from 'types/TOCContext.types';

/**
 * Enhanced Audit History that includes Page ID
 *
 * Extends the base AuditHistory interface by adding a page_id field and modified row attribute.
 * This is primarily used for API Save Changes requirements, where page_id is required
 * @interface
 */
export interface EnhancedAuditHistory extends Omit<AuditHistory, 'pk' | 'sk' | 'row'> {
  // Optional here, since new AuditHistory entities may not immediately have this
  pk?: string;
  // Optional here, since new AuditHistory entities may not immediately have this
  sk?: string;
  // Optional, as only included on api Save request
  page_id?: string;
  row: HistoryItemMetadata | string | null;
}

/**
 * Audit History Context Type definition
 * React context for Audit History state management.
 *
 * @interface
 */
export interface AuditHistoryContextType {
  deckAuditHistory: Array<EnhancedAuditHistory>;
  setDeckAuditHistory: (auditData: Array<EnhancedAuditHistory>) => void;
  pageAuditHistory: Array<EnhancedAuditHistory>;
  setPageAuditHistory: (auditData: Array<EnhancedAuditHistory>) => void;
  auditTrail: Array<EnhancedAuditHistory>;
  setAuditTrail: (auditTrail: Array<EnhancedAuditHistory>) => void;
  generatePageLibraryAuditTrail: (
    originalTocEntries: EnhancedTOCEntry[],
    createTocEntries: EnhancedTOCEntry[],
    updateTocEntries: EnhancedTOCEntry[],
    deleteTocEntries: DeleteTOCEntryData[],
  ) => Array<EnhancedAuditHistory>;
  generateDecklineAuditTrail: (
    originalDecklines: EnhancedDeckline[],
    createDecklines: EnhancedDeckline[],
    updateDecklines: EnhancedDeckline[],
    deleteDecklines: DeleteDecklineData[],
  ) => Array<EnhancedAuditHistory>;
  saveAuditTrail: (auditTrailData: Array<EnhancedAuditHistory>) => void;
  isSavingAuditHistory: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  fetchNextPageAuditHistory: () => void;
  hasNextPageAuditHistory: boolean;
  isFetchingNextPageAuditHistory: boolean;
}

export interface AuditHistoryProviderProps {
  children?: ReactNode;
}

/**
 * Enum for Audit History Change Types
 * - Expand as needed for new Audit item tracking
 *
 * @enum {string}
 */
export enum AuditHistoryChangeType {
  PAGE_CREATED = 'Page Created',
  PAGE_TOC_NAME_CHANGED = 'TOC Name Changed',
  PAGE_NAME_CHANGED = 'Page Name Changed',
  PAGE_GROUP_CHANGED = 'Page Group Changed',
  PAGE_VISIBILITY_CHANGED = 'Page Visibility Changed',
  PAGE_ACTIVE_STATE_CHANGED = 'Page Active State Changed',
  PAGE_DELETED = 'Page Deleted',
  DECKLINE_CREATED = 'Deckline Created',
  DECKLINE_NAME_CHANGED = 'Deckline Name Changed',
  DECKLINE_TYPE_CHANGED = 'Deckline Type Changed',
  DECKLINE_FORMATTING_CHANGED = 'Deckline Formatting Changed',
  KPI_CHANGED = 'KPI Changed',
  KPI_FORMULA_CHANGED = 'KPI Formula Changed',
  SUBTOTAL_FORMULA_CHANGED = 'Subtotal Formula Changed',
  DECKLINE_DELETED = 'Deckline Deleted',
}
